import { useQuery } from '@tanstack/react-query';
import { isAfter, isBefore } from 'date-fns';
import { useDispatch } from 'react-redux';
import { UrlType } from 'src/constants/constants';
import { CLIENT_TYPE } from 'src/constants/type';
import { updateScheduleDetails } from 'src/lib/redux';
import { isPPAdmin } from 'src/services/utils';

import {
  adminScheduleDetailsFetcher,
  scheduleDetailsFetcher,
} from '../../fetchers/video-player-fetcher';
import { DppSingleScheduling, HomeworkId } from '../../models/content.models';
import { SubjectId } from '../batches/useTodaySchedule';

type Props = {
  data: Record<string, any>;
  isLoading: boolean;
  refetch: any;
};

const useScheduleDetails = ({
  batchId,
  subjectSlug,
  scheduleId,
  enabled,
  onSuccess,
}: {
  batchId: string;
  subjectSlug: string;
  scheduleId: string;
  enabled: boolean;
  onSuccess?: (scheduleDetails: unknown) => void;
}): Props => {
  const dispatch = useDispatch();
  const { isLoading, data, refetch } = useQuery(
    [
      `schedule-details-${batchId}-${subjectSlug}-${scheduleId}`,
      {
        batchId,
        subjectSlug,
        scheduleId,
      },
    ],
    isPPAdmin() ? adminScheduleDetailsFetcher : scheduleDetailsFetcher,
    {
      enabled: !!enabled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 60 * 60 * 1000,
      onSuccess: (responseData) => {
        const scheduleDetails = { scheduleDetails: { ...responseData?.data } };
        dispatch(updateScheduleDetails(scheduleDetails));
        onSuccess?.(scheduleDetails.scheduleDetails);
      },
    },
  );

  return {
    isLoading,
    data: data ?? {},
    refetch,
  };
};

export class VideoDetails {
  name: string;
  image: string;
  embedCode: string;
  description: string;
  duration: string;
  _id: string;
  videoUrl: string;

  constructor(data: any) {
    data = data || {};
    this.name = data.name || '';
    this.image = data.image || '';
    this.embedCode = data.embedCode || '';
    this.description = data.description || '';
    this.duration = data.duration || '';
    this._id = data._id || '';
    this.videoUrl = data.videoUrl || '';
  }
}

class DppData {
  _id: string;
  urlType: string;

  constructor(data: any) {
    this._id = data?._id || '';
    this.urlType = data?.urlType || '';
  }
}

export class SubjectDetails {
  _id: string;
  slug: string;
  name: string;
  constructor(data: any) {
    this._id = data?._id || '';
    this.slug = data?.slug || '';
    this.name = data?.name || '';
  }
}

export class ScheduleDetails {
  _id: string;
  restrictedSchedule: boolean;
  restrictedTime: number;
  urlType: string;
  exerciseIds: any[];
  homeworkIds: HomeworkId[];
  tags: string[];
  isActive: boolean;
  isFree: boolean;
  isChatEnabled: boolean;
  isEmojiEnabled: boolean;
  isDoubtEnabled: boolean;
  isNoteEnabled: boolean;
  isCommentDisabled: boolean;
  status: string;
  batchSubjectId: string;
  day: string;
  date: string;
  startTime: string;
  endTime: string;
  classEndTime: string;
  timeline: any[];
  isDPPNotes: boolean;
  isDPPVideos: boolean;
  isPathshala: boolean;
  teachers: any[];
  id3Enabled: boolean;
  topic: string;
  meetingId: string;
  meetingPassword: string;
  url: string;
  ratingCount: number;
  subjectId: SubjectId;
  isBatchDoubtEnabled: boolean;
  dRoomId: string;
  videoDetails: VideoDetails;
  isVideoLecture: boolean;
  isLive: boolean;
  conversationId: string;
  typeId: string;
  lastWatchedPointInSec: number;
  chapterId: string;
  slug: string;
  scheduleId: string;
  rating: number;
  feedback: string;
  teacherImage: ImageId;
  lecture: Lecture;
  tagIds: any[];
  teacherName: string;
  type: string;
  subject: SubjectDetails;
  isAskSaarthiEnabled: boolean;
  isBatchAskSaarthiEnabled: boolean;
  uWebSocketEnabled: boolean;
  isBatchChatBlocked: boolean;
  isContentSecurityEnabled: boolean;
  newCdnLink: string;
  lowDataMode: boolean;
  videoAnalytics: any;
  ldmDelay: number;
  mediaChannelId: string;
  cameFrom: string | undefined;
  isPostPollFeatureEnable: boolean;
  isAwsHlsLivePlayer: boolean;
  dppSingleScheduling: DppSingleScheduling;
  dpp: DppData;
  whiteboardType: string | undefined;
  isSimulatedLecture: boolean;
  isSubtopicTimestampEnabled: boolean;
  isRecordedPollEnabled: boolean;
  isCopilotEnabled: boolean;
  isCopilotDoubtAllocationEnabled: boolean;
  batchId: string;
  constructor(data: any, cameFrom?: string) {
    data = data || {};
    this.newCdnLink = data.newCdnLink || '';
    this.isContentSecurityEnabled = data.isContentSecurityEnabled || false;
    this.isBatchAskSaarthiEnabled = data.isBatchAskSaarthiEnabled || false;
    this.id3Enabled = (data.urlType === 'awsVideo' && data.id3Enabled) || false;
    this.isAskSaarthiEnabled = data.isAskSaarthiEnabled || false;
    this.isCopilotEnabled = data.isCopilotEnabled || false;
    this.isCopilotDoubtAllocationEnabled = data.isCopilotDoubtAllocationEnabled || false;
    this.batchId = data.batchId || '';
    this.subject = new SubjectDetails(data.subject) || new SubjectDetails({});
    this.type = data.type || '';
    this.conversationId = data.conversationId || '';
    this._id = data._id || '';
    this.restrictedSchedule = data.restrictedSchedule || false;
    this.restrictedTime = data.restrictedTime || 0;
    this.urlType = data.urlType || data?.lecture?.urlType || '';
    this.exerciseIds = data?.exerciseIds || [];
    this.homeworkIds = [];
    if (data && data.homeworkIds && data.homeworkIds.length > 0) {
      const list: Array<HomeworkId> = [];
      data.homeworkIds.forEach((el: any) => {
        list.push(new HomeworkId(el));
      });
      this.homeworkIds = [...list];
    }
    this.tags = [];
    if (data && data.tags && data.tags.length > 0) {
      const list: any[] = [];
      data.tags.forEach((el: any) => {
        list.push(el);
      });
      this.tags = [...list];
    }
    this.isBatchDoubtEnabled = data.isBatchDoubtEnabled || false;
    this.ratingCount = data.ratingCount || 0;
    this.isFree = data.isFree || false;
    this.isChatEnabled = data.isChatEnabled || false;
    this.isEmojiEnabled = data.isEmojiEnabled || false;
    this.isDoubtEnabled = data.isDoubtEnabled || false;
    this.isCommentDisabled = data.isCommentDisabled || false;
    this.isNoteEnabled = !data.isNoteDisabled;
    this.status = data.status || '';
    this.batchSubjectId = data.batchSubjectId || '';
    this.day = data.data || '';
    this.date = data.date || '';
    this.startTime = data.startTime || '';
    this.endTime = data.endTime || '';
    this.classEndTime = data.classEndTime || '';
    this.timeline = data.timeline || [];
    this.isDPPNotes = data.isDPPNotes || false;
    this.isDPPVideos = data.isDPPVideos || false;
    this.isPathshala = data.isPathshala || false;
    this.teachers = data.teachers || [];
    this.topic = data.topic || '';
    this.meetingId = data.meetingId || '';
    this.meetingPassword = data.meetingPassword || '';
    this.url = data.url || data?.lecture?.url || '';
    this.isActive = data.isActive || false;
    this.videoDetails = data.videoDetails || {};
    this.typeId = data.typeId || '';
    this.isLive =
      data.isLive !== undefined
        ? data.isLive
        : (isBefore(new Date(this.startTime), new Date()) &&
            isAfter(new Date(this.endTime), new Date())) ||
          false;
    this.isPostPollFeatureEnable =
      data?.urlType === 'awsVideo' && data?.isNonLivePoll && this.isLive;
    this.subjectId = data.subjectId || {};
    this.dRoomId = data.dRoomId || '';
    this.lastWatchedPointInSec = data.lastWatchedPointInSec || 0;
    this.slug = data.slug || '';
    this.scheduleId = data.scheduleId || '';
    this.uWebSocketEnabled = data.uWebSocketEnabled || false;
    // ratingCount: number;
    // isBatchDoubtEnabled: boolean;
    // videoDetails: VideoDetails;
    this.chapterId = data.chapterId || '';
    this.rating = data.rating || 0;
    this.feedback = data.feedback || '';
    this.teacherImage = data.teacherImage || new ImageId({});
    this.lecture = data.lecture || new Lecture({});
    this.tagIds = data.tagIds || [];
    this.teacherName = data.teacherName || '';
    this.uWebSocketEnabled = data.uWebSocketEnabled || false;
    this.isBatchChatBlocked = data.isBatchChatBlocked || false;
    this.lowDataMode =
      (data.urlType === 'awsVideo' && data.whiteboardType === 'PW' && data.lowDataMode) || false;
    this.videoAnalytics = data.videoAnalytics || {};
    this.ldmDelay = data.ldmDelay || 0;
    this.mediaChannelId = data.mediaChannelId || '';
    if (cameFrom === CLIENT_TYPE.ADMIN || cameFrom === CLIENT_TYPE.TEACHER_PORTAL) {
      this.urlType = data?.urlType || data?.lecture?.urlType || '';
      this.url = data.url || data?.lecture?.url || '';
    }
    /* Simulated live lecture will not play inside AWSHLS in case of id3,ldm,postPoll */
    this.isAwsHlsLivePlayer =
      (this.id3Enabled || this.lowDataMode || this.isPostPollFeatureEnable) &&
      !data?.isSimulatedLecture;
    this.dppSingleScheduling = new DppSingleScheduling(data?.dpp || {});
    this.whiteboardType = data.whiteboardType;
    this.dpp = new DppData(data?.dpp || {});
    this.dppSingleScheduling = new DppSingleScheduling(data?.dpp || {});
    this.isVideoLecture = data.isVideoLecture || false;
    this.isSimulatedLecture = data?.isSimulatedLecture || false;
    this.isSubtopicTimestampEnabled =
      (data?.isSubtopicTimestampEnabled &&
        !this.isLive &&
        data?.subtopicTimestampStatus === 'READY') ||
      false;
    this.isRecordedPollEnabled =
      (data?.isRecordedPollEnabled &&
        !this.isLive &&
        this.status === 'COMPLETED' &&
        this.urlType === UrlType.PENPENCIL_VDO) ||
      false;
  }
}

export class Teachers {
  firstName: string;
  lastName: string;
  qualification: string;
  imageId: ImageId;
  experience: string;
  introductionVideoId: VideoDetails;

  constructor(data: any) {
    data = data || {};
    this.firstName = data.firstName || '';
    this.lastName = data.lastName || '';
    this.qualification = data.qualification || '';
    this.imageId = new ImageId(data.imageId) || new ImageId({});
    this.experience = data.experience || '';
    this.introductionVideoId = new VideoDetails(data.introductionVideoId) || new VideoDetails({});
  }
}

export class ImageId {
  _id: string;
  baseUrl: string;
  name: string;
  key: string;

  constructor(data: any) {
    data = data || {};
    this._id = data._id || '';
    this.baseUrl = data.baseUrl || '';
    this.name = data.name || '';
    this.key = data.key || '';
  }
}

export class Lecture {
  topic: string;
  urlType: string;
  publicUrl: string;
  url: string;
  lectureType: string;
  constructor(data: any) {
    data = data || {};
    this.topic = data.topic || '';
    this.urlType = data.urlType || '';
    this.publicUrl = data.publicUrl || '';
    this.url = data.url || '';
    this.lectureType = data.key || '';
  }
}

export default useScheduleDetails;
